import type { SearchPageCriteria, SearchPageFilterDefinition } from '../sharedTypes';
import {
  createDefaultFilterSelections,
  createEmptyFilterSelections,
  sanitizeFilterValues,
} from './filterState';
import type { SearchCriteriaUrlState } from './types';

export type SearchCriteriaState<FilterName extends string> = {
  queryDraft: string;
  query: string;
  selectedFilters: Record<FilterName, string[]>;
};

export const createInitialSearchCriteriaState = <FilterName extends string>(
  filterDefinitions: Record<FilterName, SearchPageFilterDefinition>,
  filterNames: FilterName[]
): SearchCriteriaState<FilterName> => ({
  queryDraft: '',
  query: '',
  selectedFilters: createDefaultFilterSelections(filterDefinitions, filterNames),
});

export const getAppliedCriteria = <FilterName extends string>(
  state: SearchCriteriaState<FilterName>
): SearchPageCriteria<FilterName> => ({
  query: state.query,
  filters: state.selectedFilters,
});

export const setSearchCriteriaDraftQuery = <FilterName extends string>(
  state: SearchCriteriaState<FilterName>,
  value: string
): SearchCriteriaState<FilterName> => ({
  ...state,
  queryDraft: value,
});

export const setSearchCriteriaFilterValues = <FilterName extends string>(
  state: SearchCriteriaState<FilterName>,
  filterDefinitions: Record<FilterName, SearchPageFilterDefinition>,
  filterName: FilterName,
  values: string[]
): SearchCriteriaState<FilterName> => ({
  ...state,
  selectedFilters: {
    ...state.selectedFilters,
    [filterName]: sanitizeFilterValues(filterDefinitions[filterName], values),
  },
});

export const toggleSearchCriteriaFilterValue = <FilterName extends string>(
  state: SearchCriteriaState<FilterName>,
  filterDefinitions: Record<FilterName, SearchPageFilterDefinition>,
  filterName: FilterName,
  value: string
): SearchCriteriaState<FilterName> => {
  const currentValues = state.selectedFilters[filterName];
  const nextValues = currentValues.includes(value)
    ? currentValues.filter((selectedValue) => selectedValue !== value)
    : [...currentValues, value];

  return setSearchCriteriaFilterValues(state, filterDefinitions, filterName, nextValues);
};

export const clearSearchCriteriaFilter = <FilterName extends string>(
  state: SearchCriteriaState<FilterName>,
  filterName: FilterName
): SearchCriteriaState<FilterName> => ({
  ...state,
  selectedFilters: {
    ...state.selectedFilters,
    [filterName]: [],
  },
});

export const clearSearchCriteriaQuery = <FilterName extends string>(
  state: SearchCriteriaState<FilterName>
): SearchCriteriaState<FilterName> => ({
  ...state,
  queryDraft: '',
  query: '',
});

export const clearAllSearchCriteriaFilters = <FilterName extends string>(
  state: SearchCriteriaState<FilterName>,
  filterNames: FilterName[]
): SearchCriteriaState<FilterName> => ({
  ...state,
  selectedFilters: createEmptyFilterSelections(filterNames),
});

export const clearAllSearchCriteria = <FilterName extends string>(
  state: SearchCriteriaState<FilterName>,
  filterNames: FilterName[]
): SearchCriteriaState<FilterName> => ({
  ...clearSearchCriteriaQuery(state),
  selectedFilters: createEmptyFilterSelections(filterNames),
});

export const submitSearchCriteriaQuery = <FilterName extends string>(
  state: SearchCriteriaState<FilterName>,
  filterNames: FilterName[],
  nextQuery = state.queryDraft
): SearchCriteriaState<FilterName> => {
  const normalizedQuery = nextQuery.trim();

  return {
    ...state,
    queryDraft: normalizedQuery,
    query: normalizedQuery,
    selectedFilters: createEmptyFilterSelections(filterNames),
  };
};

export const applySearchCriteriaUrlState = <FilterName extends string>(
  state: SearchCriteriaState<FilterName>,
  urlState: SearchCriteriaUrlState<FilterName>
): SearchCriteriaState<FilterName> => ({
  ...state,
  queryDraft: urlState.query,
  query: urlState.query,
  selectedFilters: urlState.filters,
});
